<template>
    <MultiEChartCard
        class="mb-4"
        :data="charts"
        :height="300"
        :loading="loading"
        :title="$t('dashboard.overview')"
        :range="timeRange.groupBy"
        @currentChart="setCurrentChart"
    />
</template>

<script>
    import { generateChartData } from '@/utils/chart';
    import { stringToColour } from '@/utils/util';
    import MultiEChartCard from '@/components/common/MultiEChartCard.vue';

    export default {
        name: 'Chart',
        components: { MultiEChartCard },
        inject: { parentComponent: { default: null } },
        data() {
            return {
                currentChart: 'clicks',
            };
        },
        computed: {
            items() {
                return (this.parentComponent.items || []).map(item => ({
                    ...item,
                    commission: parseFloat((item.newCommission + item.acceptedCommission).toFixed(2)),
                    publisherCommission: parseFloat((item.publisherNewCommission + item.publisherAcceptedCommission).toFixed(2)),
                }));
            },

            timeRange() {
                const { startDate, endDate, groupBy = 'day' } = this.parentComponent.filtersParams;
                return startDate && endDate ? { startDate, endDate, groupBy } : {};
            },

            chartSeries() {
                const data = {};
                this.items.forEach(item => {
                    Object.keys(item).forEach(key => {
                        if (key !== 'date') {
                            (data[key] || (data[key] = [])).push([item.date, item[key]]);
                        }
                    });
                });
                return data;
            },

            charts() {
                const makeOption = (key, labelKey, fmt, toFixed = 0) => {
                    const raw = this.chartSeries[key] || [];
                    const pts = generateChartData(this.timeRange, raw, this.timeRange.groupBy);
                    const categories = pts.map(p => this.$format.date(+p.x, 'dd MMM'));
                    const values = pts.map(p => p.y);
                    const latest = values[values.length - 1] ?? 0;

                    return {
                        name: this.$t(labelKey),
                        value: fmt(latest),
                        option: {
                            legend: { show: false },
                            tooltip: {
                                trigger: 'axis',
                                axisPointer: { type: 'shadow' },
                                formatter: params => {
                                    const pt = Array.isArray(params) ? params[0] : params;
                                    return fmt(pt.value);
                                },
                            },
                            xAxis: { type: 'category', data: categories },
                            yAxis: {
                                type: 'value',
                                axisLabel: {
                                    formatter: v => (v ? fmt(parseFloat(v).toFixed(toFixed)) : ''),
                                },
                            },
                            series: [
                                {
                                    name: this.$t(labelKey),
                                    type: 'bar',
                                    data: values,
                                    itemStyle: { color: stringToColour(key) },
                                },
                            ],
                        },
                    };
                };

                const base = {
                    clicks: makeOption('clicks', 'stat.clicks', v => Math.round(v), 0),
                    transactions: makeOption('transactions', 'stat.transactions', v => Math.round(v), 0),
                    commission: makeOption('commission', 'stat.commission', v => this.$format.currency(v), 2),
                };

                if (this.$store.getters['auth/isRoleAdmin']) {
                    base.publisherCommission = makeOption('publisherCommission', 'stat.publisherCommission', v => this.$format.currency(v), 2);

                    base.commission = makeOption('commission', 'stat.ourCommission', v => this.$format.currency(v), 2);
                }

                return base;
            },

            loading() {
                return this.parentComponent.loading;
            },
        },
        methods: {
            setCurrentChart(chartType) {
                this.currentChart = chartType;
            },
        },
    };
</script>
